// filepath: src/components/products/ProductTable.js
import React from "react";
import { CalendarDays, Package, ScanLine } from "lucide-react";
import TableActionButtons from '../../CommonComponents/TableActionButtons';
import EmptyState from '../../CommonComponents/EmptyState';
import ProductImageFallback from '../../CommonComponents/ProductImageFallback';

// Utility function to format price in PHP currency
const formatPrice = (amount) => {
    if (isNaN(amount)) return "₱0.00";
    return new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
        minimumFractionDigits: 2,
    }).format(amount);
};

// Helper component for product image with placeholder fallback and out-of-stock overlay
const ProductImage = ({ imageUrl, name, uploadsBaseURL, outOfStock, onPreview }) => {
    const [src, setSrc] = React.useState(imageUrl ? `${uploadsBaseURL}/${imageUrl}` : null);

    return (
        <button
            type="button"
            onClick={onPreview}
            className="relative h-9 w-9 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:scale-[1.03] focus:outline-none"
            aria-label={`Preview ${name} image`}
        >
            {src ? (
                <img
                    src={src}
                    alt={name}
                    className="h-full w-full object-cover"
                    onError={() => setSrc(null)}
                />
            ) : (
                <ProductImageFallback className="h-full w-full rounded-2xl" aria-label="No product image" />
            )}
            {outOfStock && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded bg-black/40">
                    <span className="text-white text-[10px] font-bold">No stock</span>
                </div>
            )}
        </button>
    );
};

// ProductTable component for admin product management
export default function ProductTable({ products = [], onEdit, onDelete, onPreviewImage, uploadsBaseURL, showActions = true }) {
    const colCount = showActions ? 7 : 6;
    return (
        <div className="overflow-x-auto rounded-[28px] border border-slate-200/80 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
            <table className="min-w-[1080px] w-full border-collapse lg:min-w-[980px] xl:min-w-0">
                <thead
                    className="border-b"
                    style={{
                        background: "linear-gradient(180deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 82%, black))",
                        borderColor: "color-mix(in srgb, var(--color-primary) 72%, black)",
                    }}
                >
                    <tr>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Item</th>
                        <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Code</th>
                        <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Price</th>
                        <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Unit</th>
                        <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Created</th>
                        <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Updated</th>
                        {showActions && (
                            <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Actions</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {products.length === 0 ? (
                        <tr>
                            <td colSpan={colCount} className="px-6 py-12 text-center">
                                <div className="flex flex-col items-center justify-center">
                                    <EmptyState
                                        icon={Package}
                                        message="No products found."
                                        size="default"
                                    />
                                </div>
                            </td>
                        </tr>
                    ) : (
                        products.map((product) => (
                            <tr
                                key={product.id}
                                className="border-b border-slate-100 bg-slate-50/85 transition hover:bg-emerald-50/70 even:bg-white last:border-0 sm:bg-transparent sm:hover:bg-slate-50/70 sm:even:bg-transparent"
                            >
                                <td className="px-4 py-2.5 xl:px-5">
                                    <div className="flex items-center gap-2.5">
                                        <ProductImage
                                            imageUrl={product.image_url}
                                            name={product.name}
                                            uploadsBaseURL={uploadsBaseURL}
                                            outOfStock={product.quantity === 0 || product.stock === 0}
                                            onPreview={() => onPreviewImage?.(product)}
                                        />
                                        <div className="min-w-0">
                                            <p className="truncate text-[13px] font-semibold leading-5 text-slate-900">{product.name}</p>
                                            <p
                                                className="max-w-[220px] truncate text-[11px] leading-4 text-slate-500 xl:max-w-[280px]"
                                                title={product.description || product.product_description || ""}
                                            >
                                                {product.description || product.product_description || "No description available"}
                                            </p>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-2.5 text-center xl:px-5">
                                    <span className="inline-flex min-w-[148px] items-center justify-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-600 shadow-sm whitespace-nowrap lg:min-w-[112px]">
                                        <ScanLine className="h-3.5 w-3.5" />
                                        {product.product_code || "-"}
                                    </span>
                                </td>
                                <td className="px-4 py-2.5 text-center xl:px-5">
                                    <div className="inline-flex min-w-[102px] items-center justify-center rounded-2xl border border-emerald-100 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 lg:min-w-[92px]">
                                        {formatPrice(product.price)}
                                    </div>
                                </td>
                                <td className="px-4 py-2.5 text-center xl:px-5">
                                    <span className="inline-flex rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs font-medium text-slate-600 shadow-sm">
                                        {product.unit_type || '-'}
                                    </span>
                                </td>
                                <td className="px-4 py-2.5 text-center xl:px-5">
                                    <span className="inline-flex min-w-[126px] items-center justify-center gap-1.5 rounded-2xl border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 whitespace-nowrap lg:min-w-[110px]">
                                        <CalendarDays className="h-3.5 w-3.5" />
                                        {new Date(product.createdAt).toLocaleDateString("en-PH", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                        })}
                                    </span>
                                </td>
                                <td className="px-4 py-2.5 text-center xl:px-5">
                                    <span className="inline-flex min-w-[126px] items-center justify-center gap-1.5 rounded-2xl border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 whitespace-nowrap lg:min-w-[110px]">
                                        <CalendarDays className="h-3.5 w-3.5" />
                                        {new Date(product.updatedAt || product.createdAt).toLocaleDateString("en-PH", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                        })}
                                    </span>
                                </td>
                                {showActions && (
                                    <td className="px-4 py-2.5 text-center xl:px-5">
                                        <div className="flex items-center justify-center gap-2">
                                            <TableActionButtons
                                                canEdit={true}
                                                canDelete={true}
                                                canToggle={false}
                                                onEdit={() => onEdit(product)}
                                                onDelete={() => onDelete(product)}
                                            />
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}
